import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ActionButtonType, TogglePayload } from 'src/app/shared/models/common';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CorporateService {
  baseUrl: string = environment.baseUrl + environment.coreUrl;
  private baseClientAccountUrl: string =
    environment.baseUrl + environment.accountUrl;

  public searchCorporateEndpoint = `${this.baseUrl}corporate/clients?page=0&pageSize=500&state=approved&sortField=last_update_time&sortOrder=DESC&filter=`;
  public searchCorporateByLoanOfficersEndpoint = (id) =>
    `${this.baseUrl}corporate/clients?loan-officer-id=${id}&page=0&pageSize=500&state=APPROVED&sort-field=lastUpdateTime&sort-order=DESC&filter=`;

  constructor(private http: HttpClient) {}

  public postCorporate(data: Object) {
    return this.http.post<any>(this.baseUrl + 'corporate/clients/', data);
  }
  public postCorporateToDraft(data: Object) {
    return this.http.post<any>(this.baseUrl + 'corporate/clients/draft', data);
  }
  public getBanks(): Observable<any> {
    return this.http.get(
      this.baseClientAccountUrl + `generic/loan-account/pbanks`,
    );
  }
  // get savings account
  public getSavingsAccount(
    id: number,
    savingsAccountType?: string,
  ): Observable<any> {
    let params = new HttpParams();

    if (savingsAccountType) {
      params = params.set('savingsAccountType', savingsAccountType);
    }

    return this.http.get(
      this.baseClientAccountUrl + 'saving-accounts/clients/' + id,
      { params },
    );
  }

  public getAllCorporate(req: any): Observable<any> {
    return this.http.get(
      this.baseUrl + `corporate/clients?${decodeURIComponent(req)}`,
    );
  }
  public getClientInfo(req: any): Observable<any> {
    return this.http.get(this.baseUrl + `franchisees/setup`);
  }
  public getOrganization(): Observable<any> {
    return this.http.get(this.baseUrl + `franchisees/tenant-and-country`);
  }

  public getCorporate(id: number): Observable<any> {
    return this.http.get(this.baseUrl + 'corporate/clients/' + id);
  }

  public getCorporateCompare(id: number): Observable<any> {
    return this.http.get(this.baseUrl + `corporate/clients/${id}/compare`);
  }

  public searchCorporate(param: string): Observable<any> {
    return this.http.get(this.searchCorporateEndpoint + param);
  }

  public updateCorporate(data: Object, id: number) {
    return this.http.put<any>(this.baseUrl + `corporate/clients/${id}`, data);
  }

  public toggleEditedCorporateClient(id, state): Observable<any> {
    return this.http.put(
      `${this.baseUrl}corporate/clients/${id}/approve-edit`,
      {
        state,
      },
    );
  }

  // Generic toggle state method
  public toggleState(data: any): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'corporate/clients/toggle', data);
  }

  // Approve method
  public Approve(data: any): Observable<any> {
    let requestPayload = data;

    if (
      data.ids &&
      Array.isArray(data.ids) &&
      data.ids[0] &&
      'companyName' in data.ids[0]
    ) {
      requestPayload = {
        ids: data.ids.map((client: any) => ({
          id: client.id,
          loanOfficerId: client.loanOfficerId,
        })),
      };
    }

    return this.http.post<any>(
      this.baseUrl + 'corporate/clients/approve',
      requestPayload,
    );
  }

  // Reject method
  public rejectClients(data: any): Observable<any> {
    let requestPayload = data;

    if (
      data.ids &&
      Array.isArray(data.ids) &&
      data.ids[0] &&
      'companyName' in data.ids[0]
    ) {
      requestPayload = {
        ids: data.ids.map((client: any) => ({
          id: client.id,
          loanOfficerId: client.loanOfficerId,
        })),
      };
    }

    return this.http.post<any>(
      this.baseUrl + 'corporate/clients/reject',
      requestPayload,
    );
  }

  // Reverse/Revert method
  public toggleClientsState(data: any): Observable<any> {
    return this.http.post<any>(
      this.baseUrl + 'corporate/clients/toggle-state',
      data,
    );
  }

  public toggle(
    type: ActionButtonType['value'],
    data: TogglePayload,
  ): Observable<any> {
    const formattedData = this.formatPayloadForAction(type, data);

    const actionEndpointMap = {
      approve: () => this.Approve(formattedData),
      reject: () => this.rejectClients(formattedData),
      disable: () => this.toggleState(formattedData),
      enable: () => this.toggleState(formattedData),
      activate: () => this.toggleState(formattedData),
      reverse: () => this.toggleClientsState(formattedData),
      revert: () => this.toggleClientsState(formattedData),
    };

    const actionHandler = actionEndpointMap[type];

    if (!actionHandler) {
      throw new Error(`Unsupported action type: ${type}`);
    }

    return actionHandler();
  }

  // Helper method to format payload based on action type
  private formatPayloadForAction(
    type: ActionButtonType['value'],
    data: any,
  ): any {
    // For approve action, use existing logic
    if (type === 'approve' || type === 'reject') {
      return data;
    }

    // For all other actions, format to backend requirements
    const formattedPayload = {
      ids: this.extractIds(data.ids),
      enable: this.getEnableValue(type),
      state: this.getStateValue(type),
      message: data.message || type,
    };

    return formattedPayload;
  }

  // Extract just the IDs from the data
  private extractIds(ids: any[]): number[] {
    if (!Array.isArray(ids)) {
      return [];
    }

    return ids
      .map((item) => {
        // If it's already a number, return it
        if (typeof item === 'number') {
          return item;
        }

        if (typeof item === 'object' && item.id) {
          return item.id;
        }
        // Fallback: try to convert to number
        return Number(item);
      })
      .filter((id) => !isNaN(id));
  }

  // Get enable value based on action type
  private getEnableValue(type: ActionButtonType['value']): boolean {
    const enableActions = ['enable', 'activate', 'approve'];
    return enableActions.includes(type);
  }

  // Get state value based on action type
  private getStateValue(type: ActionButtonType['value']): string {
    const stateMap = {
      approve: 'APPROVED',
      reject: 'REJECTED',
      disable: 'DISABLED',
      enable: 'APPROVED',
      activate: 'APPROVED',
      reverse: 'PENDING',
      revert: 'PENDING',
    };

    return stateMap[type] || 'PENDING';
  }

  // Alternative method with explicit endpoint mapping
  public handleButtonAction(buttonType: string, data: any): Observable<any> {
    const formattedData = this.formatPayloadForAction(
      buttonType as ActionButtonType['value'],
      data,
    );

    switch (buttonType) {
      case 'approve':
        return this.Approve(formattedData);

      case 'reject':
        return this.rejectClients(formattedData);

      case 'disable':
        return this.toggleState(formattedData);

      case 'enable':
      case 'activate':
        return this.toggleState(formattedData);

      case 'reverse':
      case 'revert':
        return this.toggleClientsState(formattedData);

      default:
        throw new Error(`Unknown button action: ${buttonType}`);
    }
  }

  public getCorporateClients(id: number, req: any): Observable<any> {
    return this.http.get(
      this.baseUrl + `corporate/clients/${id}/clients?${req}`,
    );
  }

  public getCorporateClientsFirst500(id: number): Observable<any> {
    return this.http.get(
      this.baseUrl + `corporate/clients/${id}/clients?page=0&pageSize=500`,
    );
  }

  public postCorporateDocument(data: Object) {
    return this.http.post<any>(
      this.baseUrl + 'corporate/clients/documents',
      data,
    );
  }

  public getCorporateDocument(req: any): Observable<any> {
    return this.http.get(this.baseUrl + `corporate/clients/documents?${req}`);
  }

  public getCorporateByBranch(id: number): Observable<any> {
    return this.http.get(
      this.baseUrl + `corporate/clients/corporateByBranch/${id}`,
    );
  }

  public getCorporateByLoanOfficer(loanOfficerId: number) {
    return this.http.get(
      this.baseUrl +
        `corporate/clients?loan-officer-id=${loanOfficerId}&page=0&pageSize=500&state=APPROVED&sort-field=lastUpdateTime&sort-order=DESC&filter=`,
    );
  }
}
